import React from 'react'
import { Card, Table, Button, Row, Col } from 'react-bootstrap'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

// 運動類型對照表
const EXERCISE_TYPES = {
  0: { name: '游泳', color: '#FF9999' },
  1: { name: '攀岩', color: '#99FF99' },
  2: { name: '走路', color: '#9999FF' },
  3: { name: '慢跑', color: '#FFFF99' },
  4: { name: '排球', color: '#FF99FF' },
  5: { name: '保齡球', color: '#99FFFF' },
  6: { name: '高爾夫球', color: '#FFB366' },
  7: { name: '乒乓球', color: '#66B3FF' },
  8: { name: '棒壘球', color: '#FF66B3' },
  9: { name: '羽球', color: '#B366FF' },
  10: { name: '網球', color: '#66FFB3' },
  11: { name: '足球', color: '#B3FF66' },
  12: { name: '籃球', color: '#66B3FF' },
  13: { name: '溜冰或直排輪', color: '#FF6666' },
  14: { name: '腳踏車或飛輪', color: '#66FF66' },
  15: { name: '瑜珈、太極等伸展律動性舞蹈', color: '#6666FF' },
  16: { name: '有氧或快節奏舞蹈', color: '#FFFF66' },
  99: { name: '其他', color: '#999999' },
}

const ExerciseRecord = ({
  data = [],
  startDate,
  endDate,
  setShowAddForm,
  onAddRecord,
}) => {
  const getLocalDateString = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  }

  // 檢查並修正時間順序，確保 start_time 是較早的時間
  const normalizeTimeOrder = (record) => {
    if (!record.start_time || !record.end_time) return record

    const startDate = new Date(record.start_time)
    const endDate = new Date(record.end_time)

    // 如果開始時間晚於結束時間，則交換
    if (startDate > endDate) {
      return {
        ...record,
        start_time: record.end_time,
        end_time: record.start_time,
      }
    }
    return record
  }

  // 計算運動時長（分鐘）
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 0
    const start = new Date(startTime)
    const end = new Date(endTime)

    const duration = end - start
    return duration > 0 ? Math.round(duration / (1000 * 60)) : 0
  }

  // 生成日期範圍
  const generateDateRange = (start, end) => {
    const dateSet = new Set()
    const startDate = new Date(start)
    const endDate = new Date(end)
    const currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      dateSet.add(currentDate.toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return Array.from(dateSet).sort()
  }

  // 處理圖表數據
  const processChartData = () => {
    if (!startDate || !endDate || !Array.isArray(data)) {
      return {
        labels: [],
        datasets: [],
      }
    }

    // Generate date range using local date string
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(getLocalDateString(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }

    const allDates = Array.from(dateSet).sort()
    const normalizedData = data.map(normalizeTimeOrder)

    // 整理每個運動類型的數據
    const exercisesByType = {}
    Object.keys(EXERCISE_TYPES).forEach((type) => {
      exercisesByType[type] = allDates.map((date) => {
        const dayRecords = normalizedData.filter((record) => {
          const endDate = new Date(record.end_time)
          return (
            record?.exercise_type === parseInt(type) &&
            getLocalDateString(endDate) === date
          )
        })

        if (!dayRecords.length) return 0

        return dayRecords.reduce(
          (sum, record) =>
            sum + calculateDuration(record.start_time, record.end_time),
          0
        )
      })
    })

    // 格式化日期標籤
    const formatDateTime = (dateStr) => {
      const date = new Date(dateStr)
      return `${date.getMonth() + 1}/${date.getDate()}`
    }

    // 創建數據集
    const datasets = Object.entries(exercisesByType)
      .filter(([_, values]) => values.some((v) => v > 0))
      .map(([type, values]) => ({
        label: EXERCISE_TYPES[type].name,
        data: values,
        backgroundColor: EXERCISE_TYPES[type].color,
        borderColor: 'white',
        borderWidth: 1,
        borderRadius: 4,
      }))

    return {
      labels: allDates.map(formatDateTime),
      datasets,
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grid: {
          color: '#CCCCCC',
          borderDash: [2, 2],
        },
        title: {
          display: true,
          text: '運動時長 (分鐘)',
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            return `${tooltipItems[0].label}`
          },
          label: (context) => {
            const value = context.raw || 0
            return `${context.dataset.label}: ${value} 分鐘`
          },
        },
      },
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 15,
        },
      },
    },
  }

  // 表格數據處理
  const getTableData = () => {
    if (!startDate || !endDate) return []

    // Generate date range using local date string
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(getLocalDateString(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }

    const allDates = Array.from(dateSet).sort(
      (a, b) => new Date(b) - new Date(a)
    )
    const normalizedData = data.map(normalizeTimeOrder)

    // 創建日期到記錄的映射
    const recordsByDate = normalizedData.reduce((acc, record) => {
      if (!record?.end_time) return acc
      const endDate = new Date(record.end_time)
      const date = getLocalDateString(endDate)
      if (!acc[date]) acc[date] = []
      acc[date].push(record)
      return acc
    }, {})

    // 為每一天創建數據結構
    return allDates.map((date) => ({
      date,
      records:
        recordsByDate[date]?.sort(
          (a, b) => new Date(b.end_time) - new Date(a.end_time)
        ) || [],
    }))
  }

  // 格式化時間
  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return '-'
    const parts = dateTimeStr.split(' ')
    return parts[1] || '-'
  }

  // 格式化日期
  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return `${dateStr} ${weekdays[date.getDay()]}`
  }

  const chartData = processChartData()
  const tableData = getTableData()

  return (
    <Row className="exercise-record g-3">
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <div className="responsive-chart">
              <Bar options={chartOptions} data={chartData} />
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <div className="table-responsive">
              {tableData.map((dayGroup) => (
                <Table
                  key={dayGroup.date}
                  hover
                  className="mb-0 border rounded-3 overflow-hidden"
                >
                  <thead>
                    <tr className="table-light">
                      <th colSpan="4">{formatDate(dayGroup.date)}</th>
                      <th className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="px-3"
                          onClick={() => {
                            setShowAddForm(true)
                            onAddRecord?.(dayGroup.date)
                          }}
                        >
                          <i className="fa-solid fa-plus me-2"></i>
                          新增
                        </Button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayGroup.records.length > 0 ? (
                      dayGroup.records.map((record) => (
                        <tr key={record.action_id || Math.random()}>
                          <td>
                            {EXERCISE_TYPES[record.exercise_type]?.name ||
                              '未知'}
                          </td>
                          <td>{formatTime(record.start_time)}</td>
                          <td>{formatTime(record.end_time)}</td>
                          <td>
                            {calculateDuration(
                              record.start_time,
                              record.end_time
                            )}{' '}
                            分鐘
                          </td>
                          <td>{record.comment || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          尚無運動記錄
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default ExerciseRecord
